import { Bell, Moon, Sun, Volume2, VolumeX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/Button";
import { useAlertTTS } from "@/shared/hooks/useAlertTTS";
import { useThemeStore, useResolvedTheme } from "@/shared/hooks/useTheme";
import { Brand } from "@/shared/ui/Brand";
import { LanguageMenu } from "./LanguageMenu";

/**
 * TopBar — search + global actions only. Identity now lives at the
 * bottom of the sidebar (see <AccountMenu />), which is where most
 * modern control-room SaaS surfaces it (Linear, Vercel, Notion).
 */
export function TopBar() {
  const { t } = useTranslation();
  const resolved = useResolvedTheme();
  const toggle = useThemeStore((s) => s.toggle);
  const ThemeIcon = resolved === "dark" ? Moon : Sun;

  const { enabled: ttsEnabled, setEnabled: setTts, supported: ttsSupported } =
    useAlertTTS();
  const TtsIcon = ttsEnabled ? Volume2 : VolumeX;

  return (
    <header
      className={
        "sticky top-0 z-30 h-[var(--header-h)] " +
        "bg-[color-mix(in_oklab,var(--bg)_82%,transparent)] backdrop-blur-md " +
        "border-b border-[var(--separator)]"
      }
    >
      <div className="h-full px-5 flex items-center gap-4">
        {/* 상단바는 유틸리티만(언어·테마·음성·알림). 좌측 네비/검색은 두지 않는다 —
            검색은 아직 미구현이라 죽은 입력창을 두느니 비운다.
            다만 **모바일에는 사이드바가 없어 로고가 어디에도 없으므로**
            좌측에 브랜드 마크를 둔다(데스크톱은 사이드바 상단에 이미 있어 중복 방지). */}
        <Brand height={18} className="lg:hidden shrink-0" />

        {/* ── Right cluster (language + theme + alerts) ──────────── */}
        <div className="ml-auto flex items-center gap-1">
          <LanguageMenu />

          <Button
            intent="ghost"
            size="icon"
            onClick={toggle}
            aria-label={resolved === "dark" ? t("widgets.topBar.switchToLight") : t("widgets.topBar.switchToDark")}
            title={resolved === "dark" ? t("widgets.topBar.toLight") : t("widgets.topBar.toDark")}
          >
            <ThemeIcon size={17} strokeWidth={1.75} />
          </Button>

          {ttsSupported && (
            <Button
              intent="ghost"
              size="icon"
              onClick={() => setTts(!ttsEnabled)}
              aria-pressed={ttsEnabled}
              aria-label={ttsEnabled ? t("widgets.topBar.voiceAlertOff") : t("widgets.topBar.voiceAlertOn")}
              title={ttsEnabled ? t("widgets.topBar.voiceAlertEnabled") : t("widgets.topBar.voiceAlertDisabled")}
              className={ttsEnabled ? "text-[var(--accent)]" : undefined}
            >
              <TtsIcon size={17} strokeWidth={1.75} />
            </Button>
          )}

          <Button intent="ghost" size="icon" aria-label={t("widgets.topBar.alerts")} title={t("widgets.topBar.alerts")}>
            <Bell size={17} strokeWidth={1.75} />
          </Button>
        </div>
      </div>
    </header>
  );
}
